import { z } from "zod";

/**
 * Why a box changed, as a `TenantBoxChanged` event names it in `reasons`. A code names the kind of
 * write that caused the change, never the write itself: an event carries no order id, user id or
 * tenant id of the change's origin, since that write can belong to another tenant.
 */
export enum BoxChangeReason {
  /** An order or appended lines reserved stock. */
  OrderPlaced = "order_placed",
  /** A whole order was cancelled and its stock released. */
  OrderCancelled = "order_cancelled",
  /** One order line was cancelled and its stock released. */
  OrderLineCancelled = "order_line_cancelled",
  /** An order or its lines moved status (packed, shipped, delivered). */
  OrderStatusChanged = "order_status_changed",
  /** A vendor listing's price, quantity or availability moved. */
  ListingUpdated = "listing_updated",
  /** A catalog item's value, price, quantity or status moved. */
  CatalogUpdated = "catalog_updated",
  /** Staff adjusted a catalog item's stock. */
  StockAdjusted = "stock_adjusted",
  /** The five-minute run over every active box, or a queue message that named no reason. */
  ScheduledRecompute = "scheduled_recompute",
  /** The box's owner or platform staff created, published or refreshed it. */
  EditorPublish = "editor_publish",
  /** A Shopify rule box rebuilt its drop table from its rules. */
  RulesRefresh = "rules_refresh",
  /** Someone set the box's status. */
  StatusChanged = "status_changed",
  /** An environment switched a box template on or off. */
  TemplateEnablement = "template_enablement",
  /** The box's owner, a Shopify admin or platform staff deleted it. */
  BoxDeleted = "box_deleted",
  /** Someone changed the box's name, art, featured flag or opener style. */
  MetadataEdited = "metadata_edited",
}

const BOX_CHANGE_REASONS = new Set<string>(Object.values(BoxChangeReason));

/** True when `value` is one of the {@link BoxChangeReason} codes. */
export function isBoxChangeReason(value: unknown): value is BoxChangeReason {
  return typeof value === "string" && BOX_CHANGE_REASONS.has(value);
}

/**
 * The presentation fields a `metadata_updated` box event names in `fields`, as
 * `GET /v1/boxes/{boxId}` names them. The event names a field, never its value.
 */
export const BOX_METADATA_FIELDS = [
  "name",
  "imageUrl",
  "art.background",
  "art.overlay",
  "art.mobile.image",
  "art.mobile.background",
  "art.mobile.overlay",
  "featured",
  "opener",
] as const;
export type BoxMetadataField = (typeof BOX_METADATA_FIELDS)[number];

const BOX_METADATA_FIELD_SET = new Set<string>(BOX_METADATA_FIELDS);

/** True when `value` is one of the {@link BOX_METADATA_FIELDS}. */
export function isBoxMetadataField(value: unknown): value is BoxMetadataField {
  return typeof value === "string" && BOX_METADATA_FIELD_SET.has(value);
}

/** The most boxes one page of `GET /v1/boxes/changes` lists, and the count when `limit` is left out. */
export const BOX_CHANGES_MAX_LIMIT = 200;
export const BOX_CHANGES_DEFAULT_LIMIT = 100;

/**
 * Query of `GET /v1/boxes/changes`: start after a `cursor` a previous page returned, or after the
 * `updatedAfter` instant on the first call. A cursor wins when both are sent.
 */
export const boxChangesQuerySchema = z
  .object({
    cursor: z.string().min(1).optional(),
    updatedAfter: z.iso.datetime({ offset: true }).optional(),
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(BOX_CHANGES_MAX_LIMIT)
      .default(BOX_CHANGES_DEFAULT_LIMIT),
  })
  .refine((query) => query.cursor !== undefined || query.updatedAfter !== undefined, {
    message: "Must provide cursor, or updatedAfter as an ISO timestamp",
    path: ["updatedAfter"],
  });

/** One box that changed: enough to find it, and `GET /v1/boxes/{boxId}` reads the rest. */
export const boxChangeSchema = z.object({
  id: z.string(),
  /** A Loot OS box template the environment switched on, rather than one of its own boxes. */
  template: z.boolean(),
  status: z.enum(["draft", "active", "archived"]),
  /** The box's current drop-table version; 0 before its first publish. */
  boxVersion: z.number().int().nonnegative(),
  /**
   * The box is gone from the environment: its owner deleted it, or the environment switched the
   * template off. A later change that brings it back lists it again with `removed` false.
   */
  removed: z.boolean(),
  /** When the box last changed for this environment, ISO 8601 in UTC with microseconds. */
  changedAt: z.string(),
});
export type BoxChange = z.infer<typeof boxChangeSchema>;

/** A page of the feed. `nextCursor` sits just past the last box; null only on an empty page. */
export const boxChangesResponseSchema = z.object({
  boxes: z.array(boxChangeSchema),
  nextCursor: z.string().nullable(),
});
export type BoxChangesPage = z.infer<typeof boxChangesResponseSchema>;
